import { AppConsts } from '@shared/AppConsts';
import { Component, Injector, ViewEncapsulation, ViewChild, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DepartmentsServiceProxy, DepartmentDto } from '@shared/service-proxies/service-proxies';
import { NotifyService } from 'abp-ng2-module';
import { AppComponentBase } from '@shared/common/app-component-base';
import { TokenAuthServiceProxy } from '@shared/service-proxies/service-proxies';
import { CreateOrEditDepartmentModalComponent } from './create-or-edit-department-modal.component';

import { appModuleAnimation } from '@shared/animations/routerTransition';
import { Table, TableModule } from 'primeng/table';
import { Paginator, PaginatorModule } from 'primeng/paginator';
import { LazyLoadEvent, PrimeTemplate } from 'primeng/api';
import { FileDownloadService } from '@shared/utils/file-download.service';
import { filter as _filter } from 'lodash-es';
import { DateTime } from 'luxon';

import { DateTimeService } from '@app/shared/common/timing/date-time.service';

import { HttpClient } from '@angular/common/http';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { finalize } from 'rxjs';

import { ExcelColumnSelectionModalComponent } from '@app/shared/common/excel-column-selection/excel-column-selection-modal';

@Component({
    templateUrl: './departments.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: [appModuleAnimation()],
    standalone: false,
})
export class DepartmentsComponent extends AppComponentBase {
    @ViewChild('createOrEditDepartmentModal', { static: true })
    createOrEditDepartmentModal: CreateOrEditDepartmentModalComponent;

    @ViewChild('dataTable', { static: true }) dataTable: Table;
    @ViewChild('paginator', { static: true }) paginator: Paginator;

    @ViewChild('ExcelFileUpload', { static: false }) excelFileUpload: FileUpload;

    @ViewChild('excelColumnSelectionModal', { static: true })
    excelColumnSelectionModal: ExcelColumnSelectionModalComponent;

    advancedFiltersAreShown = false;
    filterText = '';

    chartOfAccountDisplayPropertyFilter = '';
    chartOfAccountDisplayProperty2Filter = '';
    chartOfAccountDisplayProperty3Filter = '';
    chartOfAccountDisplayProperty4Filter = '';
    chartOfAccountDisplayProperty5Filter = '';

    uploadUrl: string;

    constructor(
        injector: Injector,
        private _departmentsServiceProxy: DepartmentsServiceProxy,
        private _notifyService: NotifyService,
        private _tokenAuth: TokenAuthServiceProxy,
        private _activatedRoute: ActivatedRoute,
        private _fileDownloadService: FileDownloadService,

        private _dateTimeService: DateTimeService,
        private _httpClient: HttpClient,
    ) {
        super(injector);

        this.uploadUrl = AppConsts.remoteServiceBaseUrl + '/Departments/ImportFromExcel';
    }

    getDepartments(event?: LazyLoadEvent) {
        if (this.primengTableHelper.shouldResetPaging(event)) {
            this.paginator.changePage(0);
            if (this.primengTableHelper.records && this.primengTableHelper.records.length > 0) {
                return;
            }
        }

        this.primengTableHelper.showLoadingIndicator();

        this._departmentsServiceProxy
            .getAll(
                this.filterText,
                this.chartOfAccountDisplayPropertyFilter,
                this.chartOfAccountDisplayProperty2Filter,
                this.chartOfAccountDisplayProperty3Filter,
                this.chartOfAccountDisplayProperty4Filter,
                this.chartOfAccountDisplayProperty5Filter,
                this.primengTableHelper.getSorting(this.dataTable),
                this.primengTableHelper.getSkipCount(this.paginator, event),
                this.primengTableHelper.getMaxResultCount(this.paginator, event),
            )
            .subscribe((result) => {
                this.primengTableHelper.totalRecordsCount = result.totalCount;
                this.primengTableHelper.records = result.items;
                this.primengTableHelper.hideLoadingIndicator();
            });
    }

    reloadPage(): void {
        this.paginator.changePage(this.paginator.getPage());
    }

    createDepartment(): void {
        this.createOrEditDepartmentModal.show();
    }

    deleteDepartment(department: DepartmentDto): void {
        this.message.confirm('', this.l('AreYouSure'), (isConfirmed) => {
            if (isConfirmed) {
                this._departmentsServiceProxy.delete(department.id).subscribe(() => {
                    this.reloadPage();
                    this.notify.success(this.l('SuccessfullyDeleted'));
                });
            }
        });
    }

    exportToExcel($event): void {
        this._departmentsServiceProxy
            .getDepartmentsToExcel(
                this.filterText,
                $event,
                this.chartOfAccountDisplayPropertyFilter,
                undefined,
                this.chartOfAccountDisplayProperty2Filter,
                undefined,
                this.chartOfAccountDisplayProperty3Filter,
                undefined,
                this.chartOfAccountDisplayProperty4Filter,
                undefined,
                this.chartOfAccountDisplayProperty5Filter,
                undefined,
            )
            .subscribe((result) => {
                this._fileDownloadService.downloadTempFile(result);
            });
    }

    uploadExcel(data: { files: File }): void {
        const formData: FormData = new FormData();
        const file = data.files[0];
        formData.append('file', file, file.name);

        this._httpClient
            .post<any>(this.uploadUrl, formData)
            .pipe(finalize(() => this.excelFileUpload.clear()))
            .subscribe((response) => {
                if (response.success) {
                    this.notify.success(this.l('ImportDepartmentsProcessStart'));
                } else if (response.error != null) {
                    this.notify.error(this.l('ImportDepartmentsUploadFailed'));
                }
            });
    }

    onUploadExcelError(): void {
        this.notify.error(this.l('ImportDepartmentsUploadFailed'));
    }

    resetFilters(): void {
        this.filterText = '';
        this.chartOfAccountDisplayPropertyFilter = '';
        this.chartOfAccountDisplayProperty2Filter = '';
        this.chartOfAccountDisplayProperty3Filter = '';
        this.chartOfAccountDisplayProperty4Filter = '';
        this.chartOfAccountDisplayProperty5Filter = '';

        this.getDepartments();
    }

    showExcelColumnSelectionModal() {
        this._departmentsServiceProxy.getDepartmentExcelColumnsToExcel().subscribe((result) => {
            this.excelColumnSelectionModal.show(result);
        });
    }
}
